import { useEffect, useState } from 'react';
import { Mail } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { getNotificationConfig, updateNotificationConfig, testEmail } from '@/api/admin';

const PRESETS: Record<string, { host: string; port: number; ssl: boolean }> = {
  Gmail:    { host: 'smtp.gmail.com',      port: 587, ssl: false },
  Outlook:  { host: 'smtp.office365.com',  port: 587, ssl: false },
  Yahoo:    { host: 'smtp.mail.yahoo.com',  port: 465, ssl: true  },
  Zoho:     { host: 'smtp.zoho.com',        port: 587, ssl: false },
  SendGrid: { host: 'smtp.sendgrid.net',    port: 587, ssl: false },
};

/** Workflow stages that raise an internal handoff email → the desk they notify. */
const WORKFLOW_STAGES: { key: string; label: string; desc: string }[] = [
  { key: 'lead', label: 'Lead captured', desc: 'Notifies the Sales / Enquiry desk' },
  { key: 'sales-order', label: 'Sales Order approved', desc: 'Notifies the Dispatch desk' },
  { key: 'pr', label: 'Purchase Requisition raised', desc: 'Notifies PR Approvers' },
  { key: 'mrn', label: 'MRN raised', desc: 'Notifies the HOD' },
  { key: 'po', label: 'Purchase Order created', desc: 'Notifies the Goods-Receipt desk' },
  { key: 'dispatch-order', label: 'Dispatch Order created', desc: 'Notifies Credit Check / Picking' },
  { key: 'grn-out', label: 'Outward GRN (dispatch)', desc: 'Notifies the Invoicing desk' },
  { key: 'weight-hold', label: 'Weight hold (dispatch)', desc: 'Notifies Super Admins & Directors to override or cancel' },
  { key: 'grn-in', label: 'Goods Receipt (GRN)', desc: 'Notifies the Stock desk' },
  { key: 'invoice', label: 'Invoice issued', desc: 'Notifies the Gate Pass desk' },
];

/** Per-stage config: on/off + optional explicit To (overrides fan-out) and extra CC. */
type StageCfg = { enabled: boolean; to: string; cc: string };

function parseStages(json: string | null): Record<string, StageCfg> {
  const out: Record<string, StageCfg> = {};
  if (!json) return out;
  try {
    const m = JSON.parse(json);
    if (m && typeof m === 'object') {
      for (const [k, v] of Object.entries(m)) {
        if (typeof v === 'boolean') out[k] = { enabled: v, to: '', cc: '' };
        else if (v && typeof v === 'object') {
          const o = v as { enabled?: boolean; to?: string; cc?: string };
          out[k] = { enabled: o.enabled !== false, to: o.to ?? '', cc: o.cc ?? '' };
        }
      }
    }
  } catch {
    /* ignore malformed config */
  }
  return out;
}

export function EmailConfigPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPass: '',
    smtpFrom: '',
    smtpFromName: '',
    smtpSsl: true,
    defaultCc: '',
    defaultBcc: '',
    autoEmailRfq: true,
    autoEmailInvoice: true,
    autoEmailPo: false,
    // Internal workflow handoff emails.
    autoWorkflowEmails: false,
    workflowStages: {} as Record<string, StageCfg>,
    // Daily Morning Management Summary (8 AM cron).
    autoDailySummary: false,
    // Dedicated invoice account — invoices go from here; other docs use the primary account.
    invoiceSmtpHost: '',
    invoiceSmtpPort: 587,
    invoiceSmtpUser: '',
    invoiceSmtpPass: '',
    invoiceSmtpFrom: '',
    invoiceSmtpFromName: '',
    invoiceSmtpSsl: true,
  });
  const [testAddr, setTestAddr] = useState('');

  const cfgQ = useQuery({ queryKey: ['notification-config'], queryFn: getNotificationConfig });

  useEffect(() => {
    if (!cfgQ.data) return;
    const d = cfgQ.data;
    setForm({
      smtpHost: d.smtpHost ?? '',
      smtpPort: d.smtpPort ?? 587,
      smtpUser: d.smtpUser ?? '',
      smtpPass: '',           // never pre-fill password
      smtpFrom: d.smtpFrom ?? '',
      smtpFromName: d.smtpFromName ?? '',
      smtpSsl: d.smtpSsl,
      defaultCc: d.defaultCc ?? '',
      defaultBcc: d.defaultBcc ?? '',
      autoEmailRfq: d.autoEmailRfq,
      autoEmailInvoice: d.autoEmailInvoice,
      autoEmailPo: d.autoEmailPo,
      autoWorkflowEmails: d.autoWorkflowEmails,
      workflowStages: parseStages(d.workflowStagesJson),
      autoDailySummary: d.autoDailySummary,
      invoiceSmtpHost: d.invoiceSmtpHost ?? '',
      invoiceSmtpPort: d.invoiceSmtpPort ?? 587,
      invoiceSmtpUser: d.invoiceSmtpUser ?? '',
      invoiceSmtpPass: '',        // never pre-fill password
      invoiceSmtpFrom: d.invoiceSmtpFrom ?? '',
      invoiceSmtpFromName: d.invoiceSmtpFromName ?? '',
      invoiceSmtpSsl: d.invoiceSmtpSsl,
    });
  }, [cfgQ.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const { workflowStages, ...rest } = form;
      return updateNotificationConfig({ ...rest, workflowStagesJson: JSON.stringify(workflowStages) });
    },
    onSuccess: () => {
      toast.push('Email configuration saved', 'success');
      qc.invalidateQueries({ queryKey: ['notification-config'] });
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Failed to save'), 'error');
    },
  });

  const testMut = useMutation({
    mutationFn: () => testEmail(testAddr),
    onSuccess: (r) => toast.push(r.message, 'success'),
    onError: (e: unknown) => {
      toast.push(apiError(e, 'SMTP test failed'), 'error');
    },
  });

  function applyPreset(name: string) {
    const p = PRESETS[name];
    setForm((f) => ({ ...f, smtpHost: p.host, smtpPort: p.port, smtpSsl: p.ssl }));
  }

  const f = form;
  const set = (key: keyof typeof form, val: unknown) => setForm((prev) => ({ ...prev, [key]: val }));

  return (
    <div>
      <SectionHeader
        title="Email Configuration"        actions={
          <Button variant="primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        }
      />

      <div data-compact-form="true" className="space-y-2">
        {/* Sender Config */}
        <Card title="Sender Configuration">
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="From Name">
              <Input value={f.smtpFromName} onChange={(e) => set('smtpFromName', e.target.value)} placeholder="e.g. Gauvin's Home Pvt Ltd" />
            </FormGroup>
            <FormGroup label="From Email">
              <Input type="email" value={f.smtpFrom} onChange={(e) => set('smtpFrom', e.target.value)} placeholder="noreply@example.com" />
            </FormGroup>
          </div>
        </Card>

        {/* Default CC/BCC */}
        <Card title="Default CC / BCC Recipients" subtitle="Add email addresses that will automatically receive a copy of all outgoing emails. Separate multiple emails with commas.">
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="Default CC Email(s)">
              <Input value={f.defaultCc} onChange={(e) => set('defaultCc', e.target.value)} placeholder="cc1@example.com, cc2@example.com" />
            </FormGroup>
            <FormGroup label="Default BCC Email(s)">
              <Input value={f.defaultBcc} onChange={(e) => set('defaultBcc', e.target.value)} placeholder="bcc@example.com" />
            </FormGroup>
          </div>
        </Card>

        {/* SMTP Server */}
        <Card title="SMTP Server — Primary Account" subtitle="Used for all documents except invoices (PO, Enquiry, Sales Order, RFQ, etc.).">

          <div className="grid grid-cols-2 gap-4 mb-4">
            <FormGroup label="SMTP Host" required>
              <Input value={f.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} placeholder="smtp.gmail.com" />
            </FormGroup>
            <FormGroup label="SMTP Port" required hint="465 = SSL/TLS · 587 = STARTTLS (recommended)">
              <Input type="number" value={f.smtpPort} onChange={(e) => {
                const port = Number(e.target.value);
                setForm((prev) => ({ ...prev, smtpPort: port, smtpSsl: port === 465 }));
              }} />
            </FormGroup>
            <FormGroup label="SMTP Username (Email)" required>
              <Input value={f.smtpUser} onChange={(e) => set('smtpUser', e.target.value)} placeholder="you@example.com" />
            </FormGroup>
            <FormGroup
              label="SMTP Password"
              hint={cfgQ.data?.smtpPassSet ? 'Password is already set. Enter a new value only to change it.' : undefined}
            >
              <Input
                type="password"
                value={f.smtpPass}
                onChange={(e) => set('smtpPass', e.target.value)}
                placeholder={cfgQ.data?.smtpPassSet ? 'Leave empty to keep existing password' : 'Enter password'}
              />
            </FormGroup>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
            <input
              type="checkbox"
              checked={f.smtpSsl}
              onChange={(e) => set('smtpSsl', e.target.checked)}
              className="w-4 h-4 accent-accent"
            />
            <span className="font-semibold">Enable SSL/TLS</span>
            <span className="text-[11px] text-[var(--text-muted)]">Only for port 465. Port 587 uses STARTTLS automatically.</span>
          </label>
        </Card>

        {/* Invoice Email Account — dedicated sender for invoices */}
        <Card
          title="Invoice Email Account"
          subtitle="Invoices are sent from this account; every other document (PO, Enquiry, Sales Order, RFQ, etc.) uses the primary account above. Leave blank to send invoices from the primary account too."
        >
          <div className="mb-4 grid grid-cols-2 gap-4">
            <FormGroup label="From Name">
              <Input value={f.invoiceSmtpFromName} onChange={(e) => set('invoiceSmtpFromName', e.target.value)} placeholder="e.g. Gauvin's Home — Billing" />
            </FormGroup>
            <FormGroup label="From Email">
              <Input type="email" value={f.invoiceSmtpFrom} onChange={(e) => set('invoiceSmtpFrom', e.target.value)} placeholder="invoices@example.com" />
            </FormGroup>
            <FormGroup label="SMTP Host">
              <Input value={f.invoiceSmtpHost} onChange={(e) => set('invoiceSmtpHost', e.target.value)} placeholder="smtp.gmail.com" />
            </FormGroup>
            <FormGroup label="SMTP Port" hint="465 = SSL/TLS · 587 = STARTTLS (recommended)">
              <Input type="number" value={f.invoiceSmtpPort} onChange={(e) => {
                const port = Number(e.target.value);
                setForm((prev) => ({ ...prev, invoiceSmtpPort: port, invoiceSmtpSsl: port === 465 }));
              }} />
            </FormGroup>
            <FormGroup label="SMTP Username (Email)">
              <Input value={f.invoiceSmtpUser} onChange={(e) => set('invoiceSmtpUser', e.target.value)} placeholder="invoices@example.com" />
            </FormGroup>
            <FormGroup
              label="SMTP Password"
              hint={cfgQ.data?.invoiceSmtpPassSet ? 'Password is already set. Enter a new value only to change it.' : undefined}
            >
              <Input
                type="password"
                value={f.invoiceSmtpPass}
                onChange={(e) => set('invoiceSmtpPass', e.target.value)}
                placeholder={cfgQ.data?.invoiceSmtpPassSet ? 'Leave empty to keep existing password' : 'Enter password'}
              />
            </FormGroup>
          </div>
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.invoiceSmtpSsl}
              onChange={(e) => set('invoiceSmtpSsl', e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            <span className="font-semibold">Enable SSL/TLS</span>
            <span className="text-[11px] text-[var(--text-muted)]">Only for port 465. Port 587 uses STARTTLS automatically.</span>
          </label>
        </Card>

        {/* Quick Setup Presets */}
        <Card title="Quick Setup Presets" subtitle="Common SMTP presets (apply to the primary account):">
          <div className="flex flex-wrap gap-2">
            {Object.keys(PRESETS).map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => applyPreset(name)}
                className="rounded border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] hover:border-accent hover:text-accent transition"
              >
                {name}
              </button>
            ))}
          </div>
        </Card>

        {/* Auto-Email Notifications */}
        <Card title="Auto-Email Notifications" subtitle="Automatically send emails when these documents are created or confirmed.">
          <div className="divide-y divide-[var(--border-soft)]">
            <AutoToggle
              label="Auto-email Invoices"
              desc="Send invoice PDF automatically when an invoice is created"
              value={f.autoEmailInvoice}
              onChange={(v) => set('autoEmailInvoice', v)}
            />
            <AutoToggle
              label="Auto-email Purchase Orders"
              desc="Send purchase order to vendor automatically"
              value={f.autoEmailPo}
              onChange={(v) => set('autoEmailPo', v)}
            />
            <AutoToggle
              label="Auto-email RFQ / Quotations"
              desc="Send RFQ link to vendors when quotation is created"
              value={f.autoEmailRfq}
              onChange={(v) => set('autoEmailRfq', v)}
            />
          </div>
        </Card>

        {/* Internal Workflow Notifications */}
        <Card
          title="Internal Workflow Notifications"
          subtitle="Automatically email the next desk when a document moves forward — the To line goes to everyone who can handle the next stage, and the person who raised the document is CC'd. Uses the primary SMTP account above."
        >
          <AutoToggle
            label="Enable workflow handoff emails"
            desc="Master switch — turn this off to stop every stage email at once"
            value={f.autoWorkflowEmails}
            onChange={(v) => set('autoWorkflowEmails', v)}
          />
          {f.autoWorkflowEmails && (
            <div className="mt-3 border-t border-[var(--border-soft)] pt-2">
              <p className="mb-2 text-[11px] text-[var(--text-muted)]">
                For each stage, turn it on/off and optionally set recipients. Leave <strong>To</strong> blank to send
                to everyone who can handle the next step (role-based). Anything in <strong>CC</strong> is added on top
                of the person who raised the document. Separate multiple emails with commas.
              </p>
              <div className="divide-y divide-[var(--border-soft)]">
                {WORKFLOW_STAGES.map((s) => {
                  const sc = f.workflowStages[s.key] ?? { enabled: true, to: '', cc: '' };
                  const patch = (p: Partial<StageCfg>) =>
                    setForm((prev) => ({
                      ...prev,
                      workflowStages: {
                        ...prev.workflowStages,
                        [s.key]: { ...(prev.workflowStages[s.key] ?? { enabled: true, to: '', cc: '' }), ...p },
                      },
                    }));
                  return (
                    <div key={s.key} className="py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-[var(--text-primary)]">{s.label}</div>
                          <div className="text-[11px] text-accent">{s.desc}</div>
                        </div>
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            checked={sc.enabled}
                            onChange={(e) => patch({ enabled: e.target.checked })}
                            className="peer sr-only"
                          />
                          <div className="h-6 w-11 rounded-full bg-[var(--border-color)] transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-accent peer-checked:after:translate-x-5" />
                        </label>
                      </div>
                      {sc.enabled && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <FormGroup label="To (override)">
                            <Input
                              value={sc.to}
                              onChange={(e) => patch({ to: e.target.value })}
                              placeholder="Blank → auto (role-based)"
                            />
                          </FormGroup>
                          <FormGroup label="CC (extra)">
                            <Input
                              value={sc.cc}
                              onChange={(e) => patch({ cc: e.target.value })}
                              placeholder="Added to the document raiser"
                            />
                          </FormGroup>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>

        {/* Daily Management Summary */}
        <Card
          title="Daily Morning Management Summary"
          subtitle="Auto-send the daily report (sales, purchase, outstanding, stock, SIS) at 8 AM to everyone holding the ‘Daily Summary’ permission — full report by email, headline + link by WhatsApp. Grant recipients in Role/User Management."
        >
          <AutoToggle
            label="Enable 8 AM daily summary"
            desc="Master switch for the scheduled send — preview it or send it now from Configurations → Email Templates → Daily Management Summary"
            value={f.autoDailySummary}
            onChange={(v) => set('autoDailySummary', v)}
          />
        </Card>

        {/* Test SMTP */}
        <Card title="Test SMTP Connection" subtitle="Send a test email to verify your SMTP configuration is working.">
          <div className="flex gap-3 items-end">
            <FormGroup label="Test Email Address" className="flex-1">
              <Input
                type="email"
                value={testAddr}
                onChange={(e) => setTestAddr(e.target.value)}
                placeholder="your@email.com"
              />
            </FormGroup>
            <Button
              variant="info"
              onClick={() => testMut.mutate()}
              disabled={testMut.isPending || !testAddr}
            >
              {testMut.isPending ? 'Sending…' : (<span className="inline-flex items-center gap-1"><Mail size={13} /> Send Test Email</span>)}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-[var(--border-soft)] bg-[var(--bg-card)] p-5">
      <h3 className="mb-1 text-sm font-extrabold text-[var(--text-primary)]">{title}</h3>
      {subtitle && <p className="mb-4 text-[11px] text-[var(--text-muted)]">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </section>
  );
}

function AutoToggle({ label, desc, value, onChange }: { label: string; desc: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="text-sm font-semibold text-[var(--text-primary)]">{label}</div>
        <div className="text-[11px] text-accent">{desc}</div>
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <div className="h-6 w-11 rounded-full bg-[var(--border-color)] peer-checked:bg-accent transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-5" />
      </label>
    </div>
  );
}
